import type { AxiosResponse } from "axios";
import { isAxiosError } from "axios";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import type { ApiResponse, BusinessLogicErrorResponse, ErrorResponse, ErrorResult } from "~/api/apiResponseTypes.ts";
import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";

export const getDataFromResponse = <T>(res: AxiosResponse<ApiResponse<T>>): T => res.data.data;

function isBusinessLogicError(data: unknown): data is BusinessLogicErrorResponse {
	return typeof data === "object" && data !== null && "reason" in data;
}

export function handleApiError(error: unknown): ErrorResult {
	if (isAxiosError(error)) {
		const errorCode = error.response?.status || 0;
		const responseData = error.response?.data;

		if (isBusinessLogicError(responseData)) {
			return {
				type: ApiResponseErrorType.BUSINESS_LOGIC,
				code: errorCode,
				reason: responseData.reason,
				data: responseData,
			};
		}

		if (errorCode === 422) {
			const validationErrors: ErrorResponse = responseData ?? { errors: {} };

			return {
				type: ApiResponseErrorType.VALIDATION,
				code: errorCode,
				messages: validationErrors.errors,
			};
		}

		return {
			type: ApiResponseErrorType.AXIOS,
			code: errorCode,
			message: error.message,
		};
	}

	return {
		type: ApiResponseErrorType.UNKNOWN,
		message: String(error),
	};
}

// The first server message of a 422, for the given fields, in order. Otherwise undefined.
export function getValidationMessage(error: unknown, fields: readonly string[]): string | undefined {
	const result = handleApiError(error);
	if (result.type !== ApiResponseErrorType.VALIDATION) return undefined;

	for (const field of fields) {
		const message = result.messages[field]?.[0]?.message;
		if (message) return message;
	}

	return undefined;
}

// Writes the server message of each listed field into react-hook-form. Returns true if one was set.
export function setServerFieldErrors<T extends FieldValues>(
	error: unknown,
	fields: readonly Path<T>[],
	setError: UseFormSetError<T>,
): boolean {
	const result = handleApiError(error);
	if (result.type !== ApiResponseErrorType.VALIDATION) return false;

	let isSet = false;
	for (const field of fields) {
		const message = result.messages[field]?.[0]?.message;
		if (message) {
			setError(field, { type: "server", message });
			isSet = true;
		}
	}

	return isSet;
}
